import { useState } from "react"
import Modal from "../Modal"
import { presetToRange, savePeriods, readStoredPeriods, addDays, PRESET_DAYS } from "../../lib/dateRange"
import type { PeriodEntry, PeriodPreset } from "../../lib/dateRange"
import { useSelectedAccount } from "../../contexts/AccountContext"

const PRESETS: { key: PeriodPreset; label: string }[] = [
  { key: "week",   label: "週" },
  { key: "month",  label: "月" },
  { key: "year",   label: "年" },
  { key: "all",    label: "全期間" },
  { key: "custom", label: "期間を設定" },
]

const fmtDate = (d: string) => d.replace(/-/g, "/")

const dayDiff = (start: string, end: string): number => {
  if (!start || !end) return 29
  return Math.round((new Date(end).getTime() - new Date(start).getTime()) / 86400000)
}

interface Props {
  periods: PeriodEntry[]
  onChange: (periods: PeriodEntry[]) => void
  storageKey: string
  allTimeStart?: string
  maxDays?: number  // 指定時、この日数を超える期間は選択不可(フリープランの45日制限用)
}

const PeriodSelectorModal = ({
  periods,
  onChange,
  storageKey,
  allTimeStart = "2000-01-01",
  maxDays,
}: Props) => {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<PeriodEntry[]>(periods)
  const { selectedAccountIds } = useSelectedAccount()

  const multiAccountActive = selectedAccountIds.length > 1
  const customStorageKey = storageKey + ".custom"

  // periods.length > 1 のときは "custom" をアクティブとして扱う
  const activePreset: PeriodPreset =
    periods.length > 1 ? "custom" : (periods[0]?.preset ?? "custom")

  const applyPresetDirectly = (preset: Exclude<PeriodPreset, "custom">) => {
    const range = presetToRange(preset, allTimeStart)
    const newPeriods: PeriodEntry[] = [{ preset, ...range }]
    savePeriods(storageKey, newPeriods)
    onChange(newPeriods)
  }

  const openModal = () => {
    if (periods.length === 1 && periods[0].preset !== "custom") {
      const stored = readStoredPeriods(customStorageKey)
      const initial: PeriodEntry[] = stored[0]?.preset === "custom"
        ? stored
        : [{ preset: "custom", ...presetToRange("month", allTimeStart) }]
      savePeriods(storageKey, initial)
      onChange(initial)
      setDraft(initial)
    } else {
      setDraft(periods)
    }
    setOpen(true)
  }

  const handlePresetClick = (preset: PeriodPreset) => {
    if (preset === "custom") {
      openModal()
    } else {
      applyPresetDirectly(preset)
    }
  }

  const p1Duration = draft[0] ? dayDiff(draft[0].startDate, draft[0].endDate) : 29

  const updateDraft = (index: number, updates: Partial<PeriodEntry>) => {
    setDraft(prev => {
      const next = prev.map((p, i) => (i === index ? { ...p, ...updates } : p))
      if (index === 0) {
        const newDur = dayDiff(next[0].startDate, next[0].endDate)
        return next.map((p, i) =>
          i === 0 ? p : { ...p, endDate: p.startDate ? addDays(p.startDate, newDur) : p.endDate }
        )
      }
      return next
    })
  }

  const addPeriod = () => {
    if (draft.length >= 3 || multiAccountActive) return
    const ref = draft[0]
    const newStart = ref.startDate ? addDays(ref.startDate, -(p1Duration + 1)) : ""
    const newEnd = newStart ? addDays(newStart, p1Duration) : ""
    setDraft(prev => [
      ...prev,
      { preset: "custom", startDate: newStart, endDate: newEnd },
    ])
  }

  const removePeriod = (index: number) => {
    setDraft(prev => prev.filter((_, i) => i !== index))
  }

  const handleApply = () => {
    savePeriods(storageKey, draft)
    savePeriods(customStorageKey, draft)
    onChange(draft)
    setOpen(false)
  }

  return (
    <>
      {/* ページ上部のプリセットボタン行 */}
      <div className="date-range-picker">
        <div className="date-range-picker__presets">
          {PRESETS.map(p => {
            const disabled = p.key !== "custom" && maxDays != null && PRESET_DAYS[p.key] > maxDays
            return (
              <button
                key={p.key}
                className={`date-range-picker__preset-btn${activePreset === p.key ? " date-range-picker__preset-btn--active" : ""}`}
                onClick={() => handlePresetClick(p.key)}
                disabled={disabled}
                title={disabled ? `フリープランでは直近${maxDays}日までの期間を選択できます` : undefined}
              >
                {p.label}
                {disabled && <i className="bi bi-lock-fill date-range-picker__preset-lock" />}
              </button>
            )
          })}
        </div>
      </div>

      {/* 期間設定モーダル */}
      {open && (
        <Modal
          title="期間を設定"
          onClose={() => setOpen(false)}
          footer={
            <>
              <button className="period-selector-modal__footer-btn" onClick={() => setOpen(false)}>キャンセル</button>
              <button className="period-selector-modal__footer-btn period-selector-modal__footer-btn--primary" onClick={handleApply}>適用</button>
            </>
          }
        >
          <div className="period-selector-modal__body">
            <div className="period-selector-modal__rows">
              {draft.map((entry, i) => {
                const isMain = i === 0
                return (
                  <div key={i} className="period-selector-modal__row">
                    <div className="period-selector-modal__row-header">
                      <span className="period-selector-modal__row-label">
                        {isMain ? "メイン期間" : `比較期間 ${i}`}
                      </span>
                      {draft.length > 1 && (
                        <button
                          className="period-selector-modal__delete-btn"
                          onClick={() => removePeriod(i)}
                          aria-label="この期間を削除"
                        >
                          <i className="bi bi-x-lg" />
                        </button>
                      )}
                    </div>

                    <div className="period-selector-modal__dates">
                      <input
                        type="date"
                        className="period-selector-modal__date-input"
                        value={entry.startDate}
                        max={isMain ? entry.endDate : undefined}
                        min={isMain && maxDays != null && entry.endDate ? addDays(entry.endDate, -(maxDays - 1)) : undefined}
                        onChange={e => {
                          if (isMain) {
                            updateDraft(0, { startDate: e.target.value })
                          } else {
                            const newEnd = e.target.value
                              ? addDays(e.target.value, p1Duration)
                              : ""
                            updateDraft(i, { startDate: e.target.value, endDate: newEnd })
                          }
                        }}
                      />
                      <span className="period-selector-modal__sep">〜</span>
                      {isMain ? (
                        <input
                          type="date"
                          className="period-selector-modal__date-input"
                          value={entry.endDate}
                          min={entry.startDate}
                          max={maxDays != null && entry.startDate ? addDays(entry.startDate, maxDays - 1) : undefined}
                          onChange={e => updateDraft(0, { endDate: e.target.value })}
                        />
                      ) : (
                        <span className="period-selector-modal__date-fixed">
                          {entry.endDate ? fmtDate(entry.endDate) : "—"}
                        </span>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            {draft.length < 3 && (
              multiAccountActive ? (
                <p className="period-selector-modal__add-disabled">
                  <i className="bi bi-info-circle" /> 複数アカウント選択中は比較期間を追加できません
                </p>
              ) : (
                <button className="period-selector-modal__add-btn" onClick={addPeriod}>
                  <i className="bi bi-plus-circle" /> 期間を追加
                </button>
              )
            )}
          </div>
        </Modal>
      )}
    </>
  )
}

export default PeriodSelectorModal
